import { Coupon } from '@dtx-company/true-common/src/types/Coupon'
import { LiteralUnion } from 'type-fest'
import { Plan } from '@dtx-company/true-common/src/types/Plan'
import { cloneErrorObject, logger } from '@dtx-company/logger'
import { fireHeapEvent } from '@dtx-company/true-common/src/utils/heap'
import { getCoupon } from '@dtx-company/inter-app/src/api/subscription-service/getCoupon'
import { promoAppliesToSelectedPlan } from '@app/common/src/components/PlanPurchaseExperience/utils/promoAppliesToSelectedPlan'
import { useCallback, useEffect, useState } from 'react'
import { useFlowRouter } from '@dtx-company/inter-app/src/hooks/useFlowRouter'

type PromoCodeState = LiteralUnion<'initial' | 'valid' | 'invalid' | 'validating', string>

/** State for everything regarding Promo codes in the Plan Purchase Experience (PPE) */
export interface PPEPromoCodeState {
  /**
   * An object representation of the promo code. It has metadata about the promo code like
   * what plans it applies too, what the exact discount percentage is, etc.
   * This value is populated from the API and its only present after the code is validated
   * at least once
   */
  promo: Coupon | undefined
  /** A string representation of the promo code. This is the value the user types in the promo code field */
  promoCode: string
  /** Set the string representation of the promo code. */
  setPromoCode: (promoCode: string) => void
  /** Validate the promo code and set the `promo` state */
  validatePromoCodeAndSet: (promoCode: string) => Promise<void>
  wasLoadedFromQuery: boolean
  /** State the promo code is in. See {@link PromoCodeState} */
  state: PromoCodeState
}

export const usePPEPromoCodeState = ({
  selectedPlan
}: {
  selectedPlan: Plan | undefined
}): PPEPromoCodeState => {
  const [promo, setPromo] = useState<Coupon>()
  const [promoCode, _setPromoCode] = useState('')
  const [state, setState] = useState<PromoCodeState>('initial')
  const [wasLoadedFromQuery, setWasLoadedFromQuery] = useState(false)
  const flowRouter = useFlowRouter()

  /** Validate promo code with the API save result in the `promo` state */
  const validatePromoCodeAndSet = useCallback(async (promoText: string) => {
    if (!promoText) return
    try {
      setState('validating')
      const promoData = await getCoupon({ code: promoText })
      const valid = promoData !== null

      if (valid) setState('valid')
      else setState('invalid')
      setPromo(promoData || undefined)
      _setPromoCode(promoText)
    } catch (err) {
      logger.logError(cloneErrorObject(err), { team: 'monetization' })
      setPromo(undefined)
    }
  }, [])

  const setPromoCode = useCallback((promoCode: string) => {
    // Reset state to initial to reset any helper text down the component tree
    setState('initial')
    // Reset `promo` to undefined because we don't know the state i.e 'valid'| 'invalid', etc of promo code
    // the user is typing until they hit Apply and we validate it.
    setPromo(undefined)
    _setPromoCode(promoCode)
  }, [])

  useEffect(() => {
    // undefined = we don't have the promo or selected plan yet so we don't know if it applies yet
    const isValidForSelectedPlan =
      promo && selectedPlan ? promoAppliesToSelectedPlan(promo, selectedPlan) : undefined

    if (isValidForSelectedPlan === true) setState('valid')
    if (isValidForSelectedPlan === false) setState('invalid')
  }, [promo, selectedPlan])

  // Track if promocode was applied
  useEffect(() => {
    if (promo?.id && selectedPlan) {
      fireHeapEvent('Revenue_Upgrade_Applied_PromoCode', {
        valid: promoAppliesToSelectedPlan(promo, selectedPlan),
        promoCode: promo?.id
      })
    }
  }, [promo, selectedPlan])

  // Load promo from query if present and not already loaded
  useEffect(() => {
    const promoFromQuery = flowRouter.getString('promoCode')
    if (promoFromQuery && !promo && !wasLoadedFromQuery) {
      validatePromoCodeAndSet(promoFromQuery)
      setWasLoadedFromQuery(true)
    }
  }, [flowRouter, wasLoadedFromQuery, promo, validatePromoCodeAndSet, setPromoCode])

  return {
    promo,
    promoCode,
    setPromoCode,
    validatePromoCodeAndSet,
    state,
    wasLoadedFromQuery
  }
}
